import { AuthQueryParamsType, AuthType } from 'config/constants/auth.constants';
import { isAuthError } from 'config/utils/errors.util';
import NavigationUtils from 'config/utils/navigation.util';
import { store } from '../../../store';
import { AuthSlice } from '../../../store/slices/auth.slice';

type PendingAction = () => void | Promise<void>;

type RequireAuthOptions = {
  redirectTo?: string;
  action?: PendingAction;
};

let pendingAction: PendingAction | null = null;
let isResuming = false;

const isAuthenticated = (): boolean => {
  const { accessToken, isAuthenticated: authenticated } = store.getState().auth;
  return Boolean(authenticated && accessToken);
};

const getCurrentLocation = (): Pick<Location, 'pathname' | 'search'> => ({
  pathname: window.location.pathname,
  search: window.location.search,
});

const setPendingAction = (action?: PendingAction): void => {
  if (action && !isResuming) {
    pendingAction = action;
  }
};

const requireAuth = ({ redirectTo, action }: RequireAuthOptions = {}): void => {
  if (isAuthenticated()) {
    action?.();
    return;
  }

  setPendingAction(action);
  const location = getCurrentLocation();
  NavigationUtils.handleAuthTypeAction(
    AuthType.LOGIN,
    true,
    null,
    location,
    redirectTo ?? NavigationUtils.getLocationRedirectPath(location.pathname, location.search),
  );
};

const withAuthRetry = async (action: PendingAction): Promise<void> => {
  try {
    await action();
  } catch (error) {
    if (isAuthError(error) && !isResuming) {
      pendingAction = action;
      return;
    }
    throw error;
  }
};

const resumeAfterAuth = async (location: Partial<Location>): Promise<void> => {
  const params = new URLSearchParams(location.search ?? window.location.search);
  const redirectTo = params.get(AuthQueryParamsType.REDIRECT_TO);
  const action = pendingAction;
  pendingAction = null;
  isResuming = true;

  store.dispatch(AuthSlice.refreshSessionExpired(false));

  const currentPathname = location.pathname ?? window.location.pathname;
  const safeRedirect = redirectTo && NavigationUtils.isSafeInternalRedirect(redirectTo) ? redirectTo : null;
  const redirectPathname = safeRedirect ? safeRedirect.split('?')[0] : null;

  NavigationUtils.handleAuthTypeAction(AuthType.LESS, false, null, location);

  if (safeRedirect && redirectPathname !== currentPathname) {
    NavigationUtils.history.navigate?.(safeRedirect, NavigationUtils.navigateOptions);
  }

  try {
    if (action) await action();
  } finally {
    isResuming = false;
  }
};

const clearIntent = (): void => {
  pendingAction = null;
};

const AuthIntentUtils = {
  requireAuth,
  withAuthRetry,
  resumeAfterAuth,
  clearIntent,
  isAuthenticated,
};

export default AuthIntentUtils;
